import { useEffect, useMemo, useRef, useState } from 'react';

import { MAX_REQUEST_LIMIT } from 'shared/hooks/paged-list/constants';
import { ListUrlStateOptions, useListUrlState } from 'shared/hooks/paged-list/use-list-url-state';
import { AsyncState, deriveAsyncState } from 'shared/utils/async-state';
import { getOffsetLimit } from 'shared/utils/pagination/page-math';

/**
 * Движок пагинации для страниц-категорий.
 *
 * Ни один списочный эндпоинт проекта не возвращает общее количество элементов,
 * поэтому число страниц узнаём единственным честным способом — **`limit + 1`**:
 * просим на один элемент больше, чем показываем, и наличие «лишнего» означает,
 * что следующая страница есть. Один запрос на страницу, правок бэкенда не нужно.
 *
 * Отсюда и нумерация — **прогрессивная**: `totalPages` растёт по мере того, как
 * пользователь уходит вглубь списка, и в пределах одного набора фильтров не
 * уменьшается — иначе пагинатор прыгал бы под курсором. Deep link `?page=5`
 * работает сразу: пятая страница грузится напрямую, а в пагинаторе появляется
 * `1…5` (и `6`, если за ней ещё что-то есть).
 *
 * Состояние нумерации живёт в `useState` этого хука, а не в module-level `Map`,
 * как у `use-block-pager-core`: там оно переживало unmount и навигацию, и список
 * открывался с числом страниц от прошлого визита.
 */

/** Минимальная форма, которой удовлетворяет любой RTK Query хук списка. */
type UseQueryHook<TData, TArgs> = (
  args: TArgs,
  options?: { skip?: boolean },
) => {
  /** Последний успешный ответ для **любого** аргумента хука. */
  data?: TData[];
  /** Ответ строго для текущего аргумента: `undefined`, пока новый запрос в пути. */
  currentData?: TData[];
  isFetching: boolean;
  isError: boolean;
  error?: unknown;
};

export interface UsePagedListOptions<TData, TArgs extends Record<string, any>> extends ListUrlStateOptions {
  /** RTK Query хук вида `useListXxxQuery`. */
  useQueryHook: UseQueryHook<TData, TArgs>;
  /** Аргументы, которые есть у запроса всегда: `{ tournamentId }` и подобное. */
  baseArgs?: Partial<TArgs>;
  /** Во что превращается строка поиска: `(search) => ({ namePart: search || undefined })`. */
  mapSearchToArgs?: (search: string) => Partial<TArgs>;
  /** Запрос пока не нужен — например, ещё неизвестна организация. */
  skip?: boolean;
  /** Ждём чего-то до запроса: показываем индикатор, а не «пусто». */
  isPending?: boolean;
}

export interface UsePagedListResult<TData> {
  /** Элементы текущей страницы — не больше `perPage` штук, «лишний» уже отрезан. */
  items: TData[];
  /**
   * Порядковый номер первого из показанных элементов минус один — то есть смещение
   * **того ответа, что сейчас на экране**, а не текущего номера страницы.
   *
   * Пока летит запрос за следующей страницей, на экране остаются прошлые элементы;
   * считать их номера от нового `page` значило бы перенумеровать неизменившийся
   * список ещё до прихода данных. Страница рисует номер как `itemsOffset + index + 1`.
   */
  itemsOffset: number;
  /** Какую ветку рисовать и надо ли пометить показанное устаревшим. */
  state: AsyncState;
  isError: boolean;
  error: unknown;
  page: number;
  perPage: number;
  /** Наибольший известный номер страницы: растёт по мере продвижения по списку. */
  totalPages: number;
  /** За текущей страницей есть ещё одна. */
  hasNext: boolean;
  /** Поисковый запрос, ушедший в аргументы запроса. */
  search: string;
  /** То, что набрано в поле ввода прямо сейчас. */
  draftSearch: string;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setSearch: (value: string) => void;
  /** Адрес этой же страницы списка с другим номером — для `<a href>` в пагинаторе. */
  buildPageHref: (page: number) => string;
}

/** Что известно про нумерацию в пределах одного набора фильтров. */
interface ScopeState {
  /** Наибольший увиденный номер страницы, включая предполагаемую следующую. */
  knownPages: number;
  /** Наибольшая страница, на которой реально были элементы, — цель отката. */
  lastLoadedPage: number;
}

const EMPTY_SCOPE: ScopeState = { knownPages: 0, lastLoadedPage: 0 };

export function usePagedList<TData, TArgs extends Record<string, any>>(
  options: UsePagedListOptions<TData, TArgs>,
): UsePagedListResult<TData> {
  const { useQueryHook, baseArgs, mapSearchToArgs, skip, isPending, storageKey, defaultPerPage, minSearchLength, searchDebounceMs } =
    options;

  const url = useListUrlState({ storageKey, defaultPerPage, minSearchLength, searchDebounceMs });
  const { page, perPage, search, setPage } = url;

  // ---- Аргументы запроса ---------------------------------------------------

  const filterArgs = useMemo(
    () => ({ ...baseArgs, ...(mapSearchToArgs ? mapSearchToArgs(search) : {}) }),
    [baseArgs, mapSearchToArgs, search],
  );

  const { offset } = getOffsetLimit(page, perPage);

  /**
   * Страница ровно в потолок бэкенда: «лишний» элемент запросить уже нельзя, он
   * будет срезан вместе с ним. Признак следующей страницы придётся спрашивать
   * отдельно — см. зонд ниже.
   */
  const needsProbe = perPage + 1 > MAX_REQUEST_LIMIT;

  // Обычно просим на один элемент больше, чем покажем: он и есть признак следующей страницы.
  const queryArgs = useMemo(
    () => ({ ...filterArgs, limit: needsProbe ? perPage : perPage + 1, offset }) as unknown as TArgs,
    [filterArgs, perPage, offset, needsProbe],
  );

  const { data, currentData, isFetching, isError, error } = useQueryHook(queryArgs, { skip });

  /**
   * Зонд: один элемент со следующей страницы. Нужен только когда `perPage + 1` не
   * влезает в потолок, и только если текущая страница пришла полной — неполная
   * сама по себе означает, что дальше ничего нет.
   *
   * Хук вызывается всегда (правило хуков), работу включает `skip`.
   */
  const isPageFull = (currentData?.length ?? 0) >= perPage;
  const isProbeEnabled = Boolean(needsProbe && isPageFull && !skip);

  const probeArgs = useMemo(
    () => ({ ...filterArgs, limit: 1, offset: offset + perPage }) as unknown as TArgs,
    [filterArgs, offset, perPage],
  );

  const probe = useQueryHook(probeArgs, { skip: !isProbeEnabled });

  const hasNextByProbe = (probe.currentData?.length ?? 0) > 0;
  // Зонд ещё в пути — выводов про следующую страницу делать рано.
  const isProbeResolved = !isProbeEnabled || (!probe.isFetching && probe.currentData !== undefined);

  // Намеренно `data`, а не `currentData`: пока летит новый запрос, на экране должен
  // остаться прошлый ответ. Что он устарел, скажет `state.isRefreshing`.
  const raw = data ?? [];
  const hasNext = needsProbe ? hasNextByProbe : raw.length > perPage;
  const items = useMemo(() => (data ?? []).slice(0, perPage), [data, perPage]);

  // Смещение того ответа, что сейчас на экране. Считается в рендере, а не в эффекте:
  // эффект отработал бы кадром позже, и один кадр новые элементы стояли бы под
  // номерами прошлой страницы. `currentData !== undefined` означает, что `data`
  // относится к текущим аргументам, то есть к текущему `offset`; пока запрос в пути,
  // на экране прошлый ответ — и смещение остаётся прошлым.
  const displayedOffsetRef = useRef(offset);
  if (currentData !== undefined) displayedOffsetRef.current = offset;
  const itemsOffset = displayedOffsetRef.current;

  // ---- Скоуп: смена фильтров обнуляет всё, что мы знали про нумерацию -------

  // Сравниваем не ссылки, а значения: `mapSearchToArgs` почти всегда приходит
  // безымянной стрелкой и меняет идентичность на каждом рендере.
  const scopeKey = useMemo(() => JSON.stringify({ filterArgs, perPage }), [filterArgs, perPage]);

  const [scope, setScope] = useState<ScopeState>(EMPTY_SCOPE);
  const scopeKeyRef = useRef(scopeKey);

  if (scopeKeyRef.current !== scopeKey) {
    // Сброс прямо в рендере, а не в эффекте: иначе один кадр пагинатор рисовал бы
    // число страниц от прошлого фильтра.
    scopeKeyRef.current = scopeKey;
    setScope(EMPTY_SCOPE);
  }

  const lastLoadedPage = scope.lastLoadedPage;

  useEffect(() => {
    // Ответ ещё не относится к текущим аргументам — выводов делать не из чего.
    if (isFetching || currentData === undefined) return;
    if (!isProbeResolved) return;

    const isEmptyPage = currentData.length === 0;
    const hasNextByResponse = needsProbe ? hasNextByProbe : currentData.length > perPage;

    setScope((prev) => {
      // Пустая страница глубже первой означает, что список короче, чем мы думали:
      // подрезаем нумерацию до последней страницы, на которой данные были.
      const next: ScopeState = isEmptyPage
        ? page > 1
          ? { knownPages: prev.lastLoadedPage, lastLoadedPage: prev.lastLoadedPage }
          : prev
        : {
            knownPages: Math.max(prev.knownPages, hasNextByResponse ? page + 1 : page),
            lastLoadedPage: Math.max(prev.lastLoadedPage, page),
          };

      const isSame = next.knownPages === prev.knownPages && next.lastLoadedPage === prev.lastLoadedPage;
      return isSame ? prev : next;
    });

    // `?page=999` на списке из трёх страниц — не «вечная загрузка», а откат на последнюю.
    if (isEmptyPage && page > 1) {
      setPage(Math.max(1, lastLoadedPage));
    }
  }, [isFetching, currentData, page, perPage, needsProbe, hasNextByProbe, isProbeResolved, lastLoadedPage, setPage]);

  // ---- Состояние рендера ---------------------------------------------------

  const state = useMemo(
    () =>
      deriveAsyncState({
        itemsCount: items.length,
        hasCurrentData: currentData !== undefined,
        isFetching,
        // Ввод уже задебаунсен — индикатор зажигается с первого символа, а не через
        // полсекунды, когда запрос наконец стартует.
        isPending: isPending || url.isSearchPending,
        isError,
      }),
    [items.length, currentData, isFetching, isPending, url.isSearchPending, isError],
  );

  return {
    items,
    itemsOffset,
    state,
    isError,
    error: error ?? null,
    page,
    perPage,
    totalPages: scope.knownPages,
    hasNext,
    search,
    draftSearch: url.draftSearch,
    setPage,
    setPerPage: url.setPerPage,
    setSearch: url.setSearch,
    buildPageHref: url.buildPageHref,
  };
}
